'use client';

import { m } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import TrojanLogo from './TrojanLogo';

interface LoadingScreenProps {
    onComplete: () => void;
    onDocked?: () => void;
}

type IntroPhase = 'checking' | 'drawing' | 'holding' | 'moving' | 'reduced';

type LogoFlight = {
    x: number;
    y: number;
    scale: number;
};

const INTRO_SESSION_KEY = 'darktrojan-intro-seen';

export default function LoadingScreen({ onComplete, onDocked }: LoadingScreenProps) {
    const [phase, setPhase] = useState<IntroPhase>('checking');
    const [logoFlight, setLogoFlight] = useState<LogoFlight>({ x: 0, y: 0, scale: 1 });
    const logoWrapperRef = useRef<HTMLDivElement>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hasCompletedRef = useRef(false);
    const hasDrawCompletedRef = useRef(false);
    const hasDockedRef = useRef(false);
    const onCompleteRef = useRef(onComplete);
    const onDockedRef = useRef(onDocked);

    useEffect(() => {
        onCompleteRef.current = onComplete;
        onDockedRef.current = onDocked;
    }, [onComplete, onDocked]);

    const completeIntro = useCallback(() => {
        if (hasCompletedRef.current) return;
        hasCompletedRef.current = true;
        try {
            window.sessionStorage.setItem(INTRO_SESSION_KEY, 'true');
        } catch {
            // The intro still works when session storage is unavailable.
        }
        onCompleteRef.current();
    }, []);

    const moveLogoToNavigation = useCallback(() => {
        const source = logoWrapperRef.current?.getBoundingClientRect();
        const target = Array.from(document.querySelectorAll<HTMLElement>('[data-nav-logo-target]'))
            .map((element) => element.getBoundingClientRect())
            .find((rect) => rect.width > 0 && rect.height > 0);

        if (source && target && target.width > 0 && target.height > 0) {
            setLogoFlight({
                x: target.left + target.width / 2 - (source.left + source.width / 2),
                y: target.top + target.height / 2 - (source.top + source.height / 2),
                scale: target.height / source.height,
            });
        } else {
            // Mobile currently has no persistent top navigation target.
            setLogoFlight({ x: 0, y: -window.innerHeight * 0.38, scale: 0.16 });
        }

        setPhase('moving');
    }, []);

    const handleLogoDocked = useCallback(() => {
        if (phase !== 'moving' || hasDockedRef.current) return;
        hasDockedRef.current = true;
        onDockedRef.current?.();
        completeIntro();
    }, [completeIntro, phase]);

    const handleDrawComplete = useCallback(() => {
        if (hasDrawCompletedRef.current) return;
        hasDrawCompletedRef.current = true;
        setPhase('holding');
        timerRef.current = setTimeout(moveLogoToNavigation, 220);
    }, [moveLogoToNavigation]);

    useEffect(() => {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        let introSeen = false;
        try {
            introSeen = window.sessionStorage.getItem(INTRO_SESSION_KEY) === 'true';
        } catch {
            // Play the intro when session storage is unavailable.
        }

        if (introSeen) {
            completeIntro();
            return;
        }

        if (prefersReducedMotion) {
            setPhase('reduced');
            timerRef.current = setTimeout(completeIntro, 350);
            return;
        }

        setPhase('drawing');
    }, [completeIntro]);

    useEffect(() => {
        if (phase === 'checking') return;

        const originalStyles = {
            overflow: document.body.style.overflow,
            position: document.body.style.position,
            width: document.body.style.width,
            height: document.body.style.height,
        };
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.height = '100%';
        document.body.classList.add('loading-active');

        const preventScrollKeys = (event: KeyboardEvent) => {
            if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Space', 'Home', 'End'].includes(event.code)) {
                event.preventDefault();
            }
        };
        document.addEventListener('keydown', preventScrollKeys);

        return () => {
            document.body.style.overflow = originalStyles.overflow;
            document.body.style.position = originalStyles.position;
            document.body.style.width = originalStyles.width;
            document.body.style.height = originalStyles.height;
            document.body.classList.remove('loading-active');
            document.body.classList.add('scrollbar-slide-in');
            document.removeEventListener('keydown', preventScrollKeys);
        };
    }, [phase]);

    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    const isMoving = phase === 'moving';
    const isReduced = phase === 'reduced';

    return (
        <m.div
            className="fixed inset-0 z-[9999] overflow-hidden"
            initial={{ opacity: 1 }}
            animate={{ opacity: isReduced ? 0 : 1 }}
            transition={{ duration: 0.3 }}
            role="status"
            aria-label="Opening DARK TR0JAN Music"
        >
            <m.div
                className="absolute inset-y-0 left-0 w-1/2 bg-black"
                animate={{ x: isMoving ? '-102%' : '0%' }}
                transition={{ duration: 0.62, ease: [0.76, 0, 0.24, 1] }}
            />
            <m.div
                className="absolute inset-y-0 right-0 w-1/2 bg-black"
                animate={{ x: isMoving ? '102%' : '0%' }}
                transition={{ duration: 0.62, ease: [0.76, 0, 0.24, 1] }}
            />

            {phase !== 'checking' && (
                <div className="fixed inset-0 flex items-center justify-center pointer-events-none">
                    <m.div
                        ref={logoWrapperRef}
                        className="relative z-10"
                        animate={{
                            x: logoFlight.x,
                            y: logoFlight.y,
                            scale: logoFlight.scale,
                            opacity: isReduced ? 0 : 1,
                        }}
                        transition={isMoving
                            ? { duration: 0.62, ease: [0.76, 0, 0.24, 1] }
                            : { duration: 0.25 }}
                        onAnimationComplete={handleLogoDocked}
                    >
                        <TrojanLogo
                            animate={false}
                            draw={phase === 'drawing' || phase === 'holding'}
                            docking={isMoving}
                            onDrawComplete={handleDrawComplete}
                        />
                    </m.div>
                </div>
            )}

        </m.div>
    );
}
